import { useEffect, useState } from "react";
import { api } from "../services/api";
import { AppSelect } from "../components/shared/FormControls";

const DEFAULT_REVENUE_STREAMS = [
  { name: "OP Consultation", gstTreatment: "exempt", isActive: true, sortOrder: 1 },
  { name: "Lab", gstTreatment: "exempt", isActive: true, sortOrder: 2 },
  { name: "Pharmacy", gstTreatment: "taxable5", isActive: true, sortOrder: 3 },
  { name: "X-Ray", gstTreatment: "exempt", isActive: true, sortOrder: 4 },
  { name: "Scan", gstTreatment: "exempt", isActive: true, sortOrder: 5 },
  { name: "ECG", gstTreatment: "exempt", isActive: true, sortOrder: 6 },
  { name: "Physiotherapy", gstTreatment: "exempt", isActive: true, sortOrder: 7 },
  { name: "Dialysis", gstTreatment: "exempt", isActive: true, sortOrder: 8 },
  { name: "OT Charges", gstTreatment: "exempt", isActive: true, sortOrder: 9 },
  { name: "Room Rent", gstTreatment: "exempt", isActive: true, sortOrder: 10 },
];

const DEFAULT_EXPENSE_HEADS = [
  { name: "Salary", category: "payroll", isActive: true, sortOrder: 1 },
  { name: "Rent", category: "operating", isActive: true, sortOrder: 2 },
  { name: "Electricity", category: "operating", isActive: true, sortOrder: 3 },
  { name: "Medicine Purchase", category: "service_cost", isActive: true, sortOrder: 4 },
  { name: "Lab Consumables", category: "service_cost", isActive: true, sortOrder: 5 },
  { name: "Professional Fees", category: "operating", tdsApplicable: true, tdsSection: "194J", tdsRate: 0.10, tdsThresholdSingle: 30000, tdsThresholdAnnual: 100000, isActive: true, sortOrder: 6 },
  { name: "Contract Charges", category: "operating", tdsApplicable: true, tdsSection: "194C", tdsRate: 0.02, tdsThresholdSingle: 30000, tdsThresholdAnnual: 100000, isActive: true, sortOrder: 7 },
  { name: "Repairs and Maintenance", category: "operating", isActive: true, sortOrder: 8 },
  { name: "Medical Equipment", category: "capital", isActive: true, sortOrder: 9 },
  { name: "Personal Expense", category: "contra", isActive: true, sortOrder: 10 },
];

const BLANK_FORM = {
  name: "Sayee Hospital",
  shortCode: "SAYEE",
  entityType: "proprietorship",
  location: "",
  pan: "",
  gstin: "",
  bankName: "HDFC",
  zohoOrgId: "",
};

export default function EntitySetup({ onEntityCreated }) {
  const [entities, setEntities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadEntities();
  }, []);

  async function loadEntities() {
    setLoading(true);
    try {
      const res = await api.get("/entities");
      setEntities(res.entities || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function update(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function saveEntity(e) {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!form.name.trim() || !form.shortCode.trim() || !form.entityType) {
      setError("Entity name, short code, and entity type are required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        shortCode: form.shortCode.trim().toUpperCase(),
        entityType: form.entityType,
        location: form.location.trim(),
        pan: form.pan.trim().toUpperCase(),
        gstin: form.gstin.trim().toUpperCase(),
        accountingSoftware: {
          type: "zoho_manual",
          zohoOrgId: form.zohoOrgId.trim(),
        },
        revenueStreams: DEFAULT_REVENUE_STREAMS,
        expenseHeads: DEFAULT_EXPENSE_HEADS,
        bankAccounts: [{
          bankName: form.bankName.trim() || "Bank",
          isDefault: true,
          zohoLedgerName: `Bank Account - ${form.bankName.trim() || "Default"}`,
        }],
      };

      const res = await api.post("/entities", payload);
      localStorage.setItem("ts360_entity", JSON.stringify(res.entity));
      setMessage(`${res.entity.name} created successfully.`);
      setShowForm(false);
      setForm(BLANK_FORM);
      await loadEntities();
      if (onEntityCreated) onEntityCreated(res.entity);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.h1}>Entities</h1>
          <p style={S.subtitle}>Create and manage hospitals or clinic entities before onboarding billing data.</p>
        </div>
        <button onClick={() => setShowForm(v => !v)} style={S.primaryBtn}>
          {showForm ? "Close" : "Add entity"}
        </button>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}
      {message && <div style={S.successBox}>{message}</div>}

      {showForm && (
        <form onSubmit={saveEntity} style={S.card}>
          <div style={S.grid}>
            <Field label="Entity name *" value={form.name} onChange={v => update("name", v)} placeholder="Sayee Hospital" />
            <Field label="Short code *" value={form.shortCode} onChange={v => update("shortCode", v)} placeholder="SAYEE" />
            <label style={S.field}>
              <span style={S.label}>Entity type *</span>
              <AppSelect
                value={form.entityType}
                onChange={value => update("entityType", value)}
                options={[
                  { value: "proprietorship", label: "Proprietorship" },
                  { value: "pvt_ltd", label: "Private Limited" },
                  { value: "partnership", label: "Partnership" },
                  { value: "llp", label: "LLP" },
                ]}
              />
            </label>
            <Field label="Location" value={form.location} onChange={v => update("location", v)} placeholder="Padappai" />
            <Field label="PAN" value={form.pan} onChange={v => update("pan", v)} placeholder="ABCDE1234F" />
            <Field label="GSTIN" value={form.gstin} onChange={v => update("gstin", v)} placeholder="Optional" />
            <Field label="Default bank" value={form.bankName} onChange={v => update("bankName", v)} placeholder="HDFC" />
            <Field label="Zoho Org ID" value={form.zohoOrgId} onChange={v => update("zohoOrgId", v)} placeholder="Optional" />
          </div>

          <div style={S.note}>
            The entity will be created with default healthcare revenue streams, expense heads, and one default bank account. You can refine these later.
          </div>

          <button type="submit" disabled={saving} style={{ ...S.primaryBtn, opacity: saving ? 0.7 : 1 }}>
            {saving ? "Saving..." : "Save entity"}
          </button>
        </form>
      )}

      <div style={S.list}>
        {loading ? (
          <div style={S.empty}>Loading entities...</div>
        ) : entities.length === 0 ? (
          <div style={S.empty}>No entities yet. Click Add entity to create Sayee Hospital.</div>
        ) : (
          entities.map(entity => (
            <div key={entity._id} style={S.entityCard}>
              <div>
                <div style={S.entityName}>{entity.name}</div>
                <div style={S.meta}>
                  {entity.shortCode} · {entity.location || "No location"} · {entity.entityType?.replace("_", " ")}
                </div>
              </div>
              <button
                onClick={() => {
                  localStorage.setItem("ts360_entity", JSON.stringify(entity));
                  if (onEntityCreated) onEntityCreated(entity);
                  setMessage(`${entity.name} selected.`);
                }}
                style={S.secondaryBtn}
              >
                Select
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder }) {
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={S.input} />
    </label>
  );
}

const S = {
  page: { maxWidth: 980, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 },
  h1: { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 6px" },
  subtitle: { fontSize: 14, color: "#607D8B", margin: 0 },
  card: { background: "#fff", borderRadius: 12, padding: 20, marginBottom: 18, boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 11, fontWeight: 700, color: "#455A64", textTransform: "uppercase", letterSpacing: ".06em" },
  input: { padding: "10px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff" },
  primaryBtn: { background: "#1B3F6E", color: "#fff", border: "none", borderRadius: 9, padding: "11px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  secondaryBtn: { background: "#EBF5FB", color: "#1B3F6E", border: "1px solid #AED6F1", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  note: { background: "#F0F4F8", color: "#607D8B", borderRadius: 8, padding: 12, fontSize: 12, margin: "16px 0" },
  list: { display: "flex", flexDirection: "column", gap: 10 },
  entityCard: { background: "#fff", borderRadius: 10, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,.07)", display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" },
  entityName: { fontSize: 15, fontWeight: 700, color: "#1B3F6E" },
  meta: { fontSize: 12, color: "#90A4AE", marginTop: 4, textTransform: "capitalize" },
  empty: { background: "#fff", borderRadius: 10, padding: 28, textAlign: "center", color: "#90A4AE", boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
  errorBox: { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "12px 16px", color: "#B71C1C", fontSize: 13, marginBottom: 12 },
  successBox: { background: "#E8F5EE", border: "1px solid #C8E6C9", borderRadius: 8, padding: "12px 16px", color: "#1A7A4A", fontSize: 13, marginBottom: 12 },
};
